import {
  AdressevaelgerAdresse,
  AdressevaelgerAdresseResponse,
  AdressevaelgerFund,
  AdressevaelgerSoegResponse,
} from "../../interfaces/adressevaelger";

/** Klimadatastyrelsens Adressevælger. Der findes endnu ingen brugerstyring:
 * `token` er obligatorisk, men enhver værdi på mindst 10 tegn accepteres, og
 * KDS anbefaler netop denne. Udskift her, når rigtig adgangsstyring kommer
 * (forventet sidst i 2026 / start 2027). */
export const ADRESSEVAELGER_BASE_URL = "https://adressevaelger.dk";
export const ADRESSEVAELGER_TOKEN = "adressevaelger123";

/** API'ets øvre grænse for søgeteksten. */
const MAKS_TEKST_LAENGDE = 73;

// Bevidst fetch og ikke api/util's axios: axios-interceptoren sætter brugerens
// Clerk-JWT og APIM-nøgle på ALLE requests, og dem skal en tredjepart ikke have.
function kald(sti: string, params: Record<string, string>) {
  const url = new URL(sti, ADRESSEVAELGER_BASE_URL);
  Object.entries({ ...params, token: ADRESSEVAELGER_TOKEN }).forEach(
    ([key, value]) => url.searchParams.set(key, value),
  );
  return fetch(url);
}

export async function soegAdresser(
  tekst: string,
  maksimum = 50,
): Promise<AdressevaelgerFund[]> {
  const trimmet = tekst.trim().slice(0, MAKS_TEKST_LAENGDE);
  if (!trimmet) return [];
  const response = await kald("/adresser/soeg", {
    tekst: trimmet,
    maksimum: String(maksimum),
  });
  if (!response.ok) {
    throw new Error(`Adressevælger-søgning fejlede (${response.status})`);
  }
  const data = (await response.json()) as AdressevaelgerSoegResponse;
  if (data.status !== "ok") {
    throw new Error(`Adressevælger-søgning fejlede: ${data.beskrivelse}`);
  }
  return data.fund ?? [];
}

/** Slår en DAR-adresse op på id. Returnerer null, hvis id'et ikke er en
 * (kendt) DAR_Adresse — fx et husnummer-id. */
export async function hentAdresse(
  id: string,
): Promise<AdressevaelgerAdresse | null> {
  const response = await kald(`/adresser/${encodeURIComponent(id)}`, {});
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Adressevælger-opslag fejlede (${response.status})`);
  }
  const data = (await response.json()) as AdressevaelgerAdresseResponse;
  return data.status === "ok" && data.adresse ? data.adresse : null;
}

/** Unikke vejnavne fra et søgeresultat. Adressevælgeren har intet rent
 * vejnavne-endpoint, så navnene samles fra de delvise fund. */
export function vejnavneFraFund(fund: AdressevaelgerFund[]): string[] {
  const navne = fund
    .map((f) =>
      f.type === "vejnavn"
        ? f.vejNavn
        : f.type === "husnummer" || f.type === "navngivenvejpostnummer"
          ? f.vejnavn
          : undefined,
    )
    .filter((navn): navn is string => !!navn);
  return [...new Set(navne)];
}
